import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { TAppDispatch, TRootState } from '#/redux/store';
import ApiManager from '#/services/api/ApiManager';
import { apiConfig } from '#/utils/constants';
import generateAuthParams from '#/utils/generateHash';
import { CharactersActionType } from '#/redux/actions/characters';

import CharacterPreview from '#/components/layout/CharacterPreview';
import Pagination from '#/components/layout/Pagination/Pagination';
import './Characters.style.scss';
import AutoSuggestSelect from '#/components/form/AutoSuggestSelect/AutoSuggestSelect';
import { IProps as ISelectProps } from '#/components/form/Select/Select';

import Input from '#/components/form/Input/Input';

import {
  calculateOffset,
  calculateTotalPages,
  debounce,
} from '#/utils/functions';
import { useNavigate } from 'react-router-dom';
import { RoutesNames } from '#/router/routes';
import CharactersLayout from '#/components/layout/CharactersLayout/CharactersLayout';
import { IMarvelAPIResponse } from '#/types/common';
import ICharacter from '#/types/characters';
import IComic from '#/types/comics';
import IStory from '#/types/stories';
import ContextMenuLayer, {
  IContextMenuOption,
} from '#/components/layout/ContextMenuLayer/ContextMenuLayer';
import { BookMarksActionType } from '#/redux/actions/bookmarks';
import { HidenItemsActionType } from '#/redux/actions/hidenItems';
import Button from '#/components/form/Button/Button';
import NoDataFound from '#/components/layout/NoDataFound/NoDataFound';
import { useError } from '#/hooks/useError/useError';

const Characters = () => {
  const limit = 20;

  const loadings = useRef(0);

  const { addError } = useError();

  const characterSelector = (state: TRootState) => {
    return state.characters;
  };

  const bookmarksSelector = (state: TRootState) => {
    const bookmarkCharacters = state.bookmarks.bookmarks.filter(
      (bookmark) => bookmark.type === 'character'
    );
    return bookmarkCharacters;
  };

  const hidenItemsSelector = (state: TRootState) => {
    const hidenItemsCharacters = state.hidenItems.hidenItems.filter(
      (hidenItem) => hidenItem.type === 'character'
    );
    return hidenItemsCharacters;
  };

  const charactersState = useSelector(characterSelector);

  const hidenCharacters = useSelector(hidenItemsSelector);

  const bookmarkedCharacters = useSelector(bookmarksSelector);

  const {
    characters,
    search,
    comics,
    selectedComic,
    stories,
    selectedStory,
    page,
    totalPages,
    loading,
  } = charactersState;

  const dispatch = useDispatch<TAppDispatch>();

  const navigate = useNavigate();

  const getCharacters = useCallback(
    async (
      currentPage: number,
      currentSelectedComic: number | undefined,
      currentSelectedStory: number | undefined,
      currentSearch: string
    ) => {
      loadings.current++;

      const apiManager = new ApiManager(apiConfig);

      const newCharacters = await apiManager.getAllEntities<
        IMarvelAPIResponse<ICharacter>
      >('CHARACTERS', {
        ...generateAuthParams(),
        limit: limit,
        offset: calculateOffset(currentPage, limit),
        comics: currentSelectedComic,
        stories: currentSelectedStory,
        nameStartsWith: currentSearch ? currentSearch : undefined,
      });

      loadings.current--;

      if (loadings.current === 0) {
        dispatch({ type: CharactersActionType.CHARACTER_STOP_LOADING });
      }
      if ('error' in newCharacters) {
        addError(newCharacters.error);
      } else {
        if (newCharacters.data.data?.results) {
          dispatch({
            type: CharactersActionType.CHARACTER_SET_CHARACTERS,
            payload: newCharacters.data.data.results,
          });
        }

        if (
          newCharacters.data.data?.total != undefined &&
          newCharacters.data.data.limit != undefined
        ) {
          dispatch({
            type: CharactersActionType.CHARACTER_SET_TOTAL_PAGES,
            payload: calculateTotalPages(
              newCharacters.data.data.total,
              newCharacters.data.data.limit
            ),
          });
        }
      }
    },
    [addError, dispatch]
  );

  const debouncedGetCharacters = useMemo(
    () => debounce(getCharacters, 750),
    [getCharacters]
  );

  useEffect(() => {
    dispatch({ type: CharactersActionType.CHARACTER_START_LOADING });
    debouncedGetCharacters(page, selectedComic, selectedStory, search);
  }, [
    debouncedGetCharacters,
    dispatch,
    page,
    search,
    selectedComic,
    selectedStory,
  ]);

  useEffect(() => {
    dispatch({ type: CharactersActionType.CHARACTER_SET_PAGE, payload: 1 });
  }, [dispatch, search, selectedComic, selectedStory]);

  useEffect(() => {
    // Avengers
    const seriesWithResults = 22_547;

    const getComics = async () => {
      const apiManager = new ApiManager(apiConfig);
      loadings.current++;
      const newComics = await apiManager.getAllEntities<
        IMarvelAPIResponse<IComic>
      >('COMICS', {
        ...generateAuthParams(),
        limit: 15,
        series: seriesWithResults,
        orderBy: '-modified',
      });
      loadings.current--;
      if (loadings.current === 0) {
        dispatch({ type: CharactersActionType.CHARACTER_STOP_LOADING });
      }
      if ('error' in newComics) {
        addError(newComics.error);
      } else {
        if (newComics.data.data?.results) {
          dispatch({
            type: CharactersActionType.CHARACTER_SET_COMICS,
            payload: newComics.data.data.results,
          });
        }
      }
    };
    dispatch({ type: CharactersActionType.CHARACTER_START_LOADING });
    getComics();

    const getStories = async () => {
      const apiManager = new ApiManager(apiConfig);
      loadings.current++;
      const newStories = await apiManager.getAllEntities<
        IMarvelAPIResponse<IStory>
      >('STORIES', {
        ...generateAuthParams(),
        limit: 15,
        series: seriesWithResults,
        orderBy: '-modified',
      });
      loadings.current--;
      if (loadings.current === 0) {
        dispatch({ type: CharactersActionType.CHARACTER_STOP_LOADING });
      }
      if ('error' in newStories) {
        addError(newStories.error);
      } else {
        if (newStories.data.data?.results) {
          dispatch({
            type: CharactersActionType.CHARACTER_SET_STORIES,
            payload: newStories.data.data.results,
          });
        }
      }
    };
    dispatch({ type: CharactersActionType.CHARACTER_START_LOADING });
    getStories();
  }, [addError, dispatch]);

  const handleSearchChange = (value: string) => {
    dispatch({ type: CharactersActionType.CHARACTER_START_LOADING });
    dispatch({
      type: CharactersActionType.CHARACTER_SET_SEARCH,
      payload: value,
    });
  };

  const handleComicChange = (value: ISelectProps['value']) => {
    if (typeof value === 'number' || value === undefined) {
      dispatch({ type: CharactersActionType.CHARACTER_START_LOADING });
      dispatch({
        type: CharactersActionType.CHARACTER_SET_SELECTED_COMIC,
        payload: value,
      });
    }
  };

  const handleStoryChange = (value: ISelectProps['value']) => {
    if (typeof value === 'number' || value === undefined) {
      dispatch({ type: CharactersActionType.CHARACTER_START_LOADING });
      dispatch({
        type: CharactersActionType.CHARACTER_SET_SELECTED_STORY,
        payload: value,
      });
    }
  };

  const handlePageClick = (newPage: number) => {
    dispatch({ type: CharactersActionType.CHARACTER_START_LOADING });
    dispatch({
      type: CharactersActionType.CHARACTER_SET_PAGE,
      payload: newPage,
    });
  };

  const handleShowHiden = () => {
    dispatch({
      type: HidenItemsActionType.HIDEN_ITEMS_REMOVE_ALL_HIDEN_ITEMS,
      payload: 'character',
    });
  };

  return (
    <div>
      <div className="characters--filters">
        <div className="characters--filter">
          <label>Name:</label>
          <Input
            data-testid="searchCharacter"
            value={search}
            handleOnChange={handleSearchChange}
          />
        </div>
        <div className="characters--filter">
          <label>Comic:</label>
          <AutoSuggestSelect
            testId="comicSelect"
            value={selectedComic}
            handleOnChange={handleComicChange}
            options={comics.map((comic) => {
              if (comic.title != undefined && comic.id != undefined) {
                return {
                  text: comic.title,
                  value: comic.id,
                };
              } else {
                return {
                  text: 'Comic without title',
                  value: '',
                };
              }
            })}
          />
        </div>
        <div className="characters--filter">
          <label>Story:</label>
          <AutoSuggestSelect
            value={selectedStory}
            handleOnChange={handleStoryChange}
            options={stories.map((story) => {
              if (story.title && story.id) {
                return {
                  text: story.title,
                  value: story.id,
                };
              } else {
                return {
                  text: 'Story without title',
                  value: '',
                };
              }
            })}
          />
        </div>
      </div>
      {hidenCharacters.length > 0 && (
        <div className="characters--filters">
          {characters.find(
            (character) =>
              hidenCharacters.find(
                (hidenCharacter) => hidenCharacter.id === character.id
              ) !== undefined
          ) !== undefined && (
            <div className="characters--filter">
              <span>There are hiden elements in this page.</span>
            </div>
          )}
          <div className="characters--filter">
            <Button
              text="Show hiden"
              handleOnClick={handleShowHiden}
              type="danger"
            />
          </div>
        </div>
      )}
      {characters.length > 0 || loading ? (
        <CharactersLayout>
          {loading
            ? [...Array(limit).keys()].map((loadingCharacter) => (
                <CharacterPreview key={loadingCharacter} skeleton />
              ))
            : characters.map((character) => {
                const characterIsHiden =
                  hidenCharacters.find(
                    (hidenCharacter) => hidenCharacter.id === character.id
                  ) !== undefined;
                if (characterIsHiden) {
                  return null;
                }
                const characterIsBookmarked =
                  bookmarkedCharacters.find(
                    (bookmarkedCharacter) =>
                      bookmarkedCharacter.id === character.id
                  ) !== undefined;
                const handleCharacterClick = () =>
                  navigate(`${RoutesNames.CHARACTERS}/${character.id}`);
                const handleAddToBookmark = () => {
                  if (character.id) {
                    dispatch({
                      type: BookMarksActionType.BOOKMARKS_ADD_BOOKMARK,
                      payload: { type: 'character', id: character.id },
                    });
                  }
                };
                const handleRemoveFromBookmark = () => {
                  if (character.id) {
                    dispatch({
                      type: BookMarksActionType.BOOKMARKS_REMOVE_BOOKMARK,
                      payload: { type: 'character', id: character.id },
                    });
                  }
                };
                const handleAddToHidenItems = () => {
                  if (character.id) {
                    dispatch({
                      type: HidenItemsActionType.HIDEN_ITEMS_ADD_HIDEN_ITEM,
                      payload: { type: 'character', id: character.id },
                    });
                  }
                };
                const options: IContextMenuOption[] = [
                  {
                    text: 'Hide',
                    onClick: handleAddToHidenItems,
                  },
                ];
                if (characterIsBookmarked) {
                  options.push({
                    text: 'Remove from bookmarks',
                    onClick: handleRemoveFromBookmark,
                  });
                } else {
                  options.push({
                    text: 'Add to bookmarks',
                    onClick: handleAddToBookmark,
                  });
                }
                return (
                  <ContextMenuLayer key={character.id} options={options}>
                    <CharacterPreview
                      handleOnClick={handleCharacterClick}
                      image={`${character.thumbnail?.path}/portrait_xlarge.${character.thumbnail?.extension}`}
                      name={character.name ? character.name : ''}
                      bookmarked={characterIsBookmarked}
                    />
                  </ContextMenuLayer>
                );
              })}
        </CharactersLayout>
      ) : (
        <NoDataFound />
      )}
      <div className="characters--pagination--container">
        <Pagination
          page={page}
          pages={totalPages}
          handlePageClick={handlePageClick}
        />
      </div>
    </div>
  );
};

export default Characters;
